/**
 * Google Ads tier-gated KPI dashboard API client.
 *
 * Client-facing per-tenant dashboard: Tier 1 metrics are always shown, Tier 2
 * metrics are gated by the tenant's product tier (>= 2 unlocks them).
 *
 * ⚠️ The exact backend path prefix is still being finalized. Both endpoint URLs
 * live as named constants below so they are trivial to reconcile — reference
 * these constants everywhere; never inline the path.
 */
import { apiClient } from './apiClient';

// ---------------------------------------------------------------------------
// Endpoint URLs — reconcile here after the backend agent reports the prefix.
// ---------------------------------------------------------------------------
export const KPI_DASHBOARD_URL = '/google-ads/monthly/kpi/dashboard';
export const KPI_TENANT_TIER_URL = '/google-ads/monthly/kpi/tenant-tier';
export const KPI_TIMESERIES_URL = '/google-ads/monthly/kpi/timeseries';

// ---------------------------------------------------------------------------
// Types (frozen contract)
// ---------------------------------------------------------------------------

/** All Tier 1 + Tier 2 metric keys. Tier 2 keys are optional/nullable. */
export interface KpiDashboardMetrics {
  // Tier 1 — always present
  spend_usd?: number | null;
  conversions?: number | null;
  /**
   * Conversions in lead categories only — a call, form, quote request or
   * booking, not every tracked action.
   *
   * null means the lead query could not be read. That is NOT zero leads, and
   * must render as n/a: reporting a failed query as "0 leads" would tell a
   * client they had a dead month.
   */
  leads?: number | null;
  cost_per_lead_usd?: number | null;
  cost_per_conversion_usd?: number | null;
  /**
   * Total daily budget across ENABLED campaigns, deduped by shared budget.
   *
   * Point-in-time, not a period metric: Google exposes no budget history, so
   * the PREVIOUS period is always null and its delta is null too. That is
   * deliberate — repeating today's number under last month's label would imply
   * the budget never changed.
   */
  daily_budget_usd?: number | null;
  /** Spend as a share of (daily budget x days in period). */
  budget_utilisation?: number | null;
  clicks?: number | null;
  ctr?: number | null;
  cpc_usd?: number | null;
  impressions?: number | null;
  conversion_rate?: number | null;
  // Tier 2 — only present when unlocked (product_tier >= 2)
  impression_share?: number | null;
  lost_impression_share_budget?: number | null;
  lost_impression_share_rank?: number | null;
  /** False when there was no spend/data in the period. */
  available?: boolean;
}

export interface KpiDashboardDelta {
  absolute: number | null;
  percent: number | null;
}

export interface KpiDashboardPeriod {
  current_start: string;
  current_end: string;
  previous_start: string;
  previous_end: string;
}

export interface KpiDashboardTier1 {
  current: KpiDashboardMetrics;
  previous: KpiDashboardMetrics;
  deltas: Record<string, KpiDashboardDelta>;
}

export interface KpiDashboardTier2 {
  unlocked: boolean;
  current: KpiDashboardMetrics | null;
  previous: KpiDashboardMetrics | null;
  deltas: Record<string, KpiDashboardDelta> | null;
}

export interface KpiDashboard {
  tenant_id: string;
  /** The tenant's REAL persisted product tier (never affected by preview). */
  product_tier: number;
  /**
   * The tier actually used for gating this payload. Equals `product_tier`
   * normally; equals the requested `preview_tier` when an operator previews
   * a different tier. The UI labels a "PREVIEW" chip when they differ.
   */
  effective_tier: number;
  period: KpiDashboardPeriod;
  tier1: KpiDashboardTier1;
  tier2: KpiDashboardTier2;
  client_safe_summary: string;
  generated_at: string;
}

export interface TenantTierResult {
  tenant_id: string;
  product_tier: number;
}

/**
 * One calendar day of account metrics.
 *
 * The backend returns a DENSE series — Google drops zero-activity days and the
 * service zero-fills them — so every day in the window is present and the chart
 * never has to interpolate a gap. Only these four measures exist per day; every
 * ratio (CTR, CPC, cost/conversion) is derived from them client-side.
 */
export interface KpiTimeseriesPoint {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  spend_usd: number;
  clicks: number;
  impressions: number;
  conversions: number;
}

export interface KpiTimeseriesResult {
  tenant_id: string;
  customer_id: string;
  period_start: string;
  period_end: string;
  /**
   * Empty when the Google Ads fetch failed — the backend swallows the error and
   * returns [] rather than raising, so [] is "no data", NOT "request failed".
   * Callers must render it as an empty state, never as an error.
   */
  series: KpiTimeseriesPoint[];
}

/**
 * Daily KPI series for the dashboard chart.
 *
 * `periodStart`/`periodEnd` are optional on the wire (the backend defaults to
 * the same last-complete-30-days window as the dashboard tiles). Pass the
 * dashboard's own window explicitly so the chart and the tiles are provably
 * describing the same days.
 */
export async function getKpiTimeseries(
  tenantId: string,
  periodStart?: string,
  periodEnd?: string,
): Promise<KpiTimeseriesResult> {
  const { data } = await apiClient.get<KpiTimeseriesResult>(
    KPI_TIMESERIES_URL,
    {
      params: {
        tenant_id: tenantId,
        ...(periodStart ? { period_start: periodStart } : {}),
        ...(periodEnd ? { period_end: periodEnd } : {}),
      },
      // Shares the dashboard's 10-minute backend cache, but a cold call still
      // does a live Google Ads query — the default 30s axios timeout can trip.
      timeout: 90_000,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function getKpiDashboard(
  tenantId: string,
  previewTier?: number,
): Promise<KpiDashboard> {
  const { data } = await apiClient.get<KpiDashboard>(KPI_DASHBOARD_URL, {
    params: {
      tenant_id: tenantId,
      // Operator-only ephemeral tier preview. Only sent when explicitly set so
      // the default (real-tier) request is byte-identical to what it was.
      ...(previewTier != null ? { preview_tier: previewTier } : {}),
    },
    // A cold load does two live Google Ads queries; the default 30s axios
    // timeout can trip before they finish. Backend caches for 10min, so this
    // longer ceiling only bites on the first (uncached) load.
    timeout: 90_000,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Persisted snapshot (stale-while-revalidate)
//
// A cold dashboard load does two live Google Ads queries (~5.5s). Persisting the
// last successful payload to localStorage lets the tab paint instantly from the
// snapshot on the next mount while a fresh fetch revalidates in the background.
// ---------------------------------------------------------------------------

const SNAPSHOT_KEY_PREFIX = 'kpi-dashboard-snapshot:';

/** Read the last persisted dashboard for a tenant, or undefined on any miss/error. */
export function readDashboardSnapshot(
  tenantId: string,
): KpiDashboard | undefined {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY_PREFIX + tenantId);
    if (!raw) return undefined;
    return JSON.parse(raw) as KpiDashboard;
  } catch {
    return undefined;
  }
}

/** Persist a fresh dashboard payload for a tenant. Quota/serialization errors are ignored. */
export function writeDashboardSnapshot(
  tenantId: string,
  data: KpiDashboard,
): void {
  try {
    localStorage.setItem(SNAPSHOT_KEY_PREFIX + tenantId, JSON.stringify(data));
  } catch {
    // Ignore quota / serialization errors — the snapshot is a best-effort cache.
  }
}

export async function getTenantTier(
  tenantId: string,
): Promise<TenantTierResult> {
  const { data } = await apiClient.get<TenantTierResult>(KPI_TENANT_TIER_URL, {
    params: { tenant_id: tenantId },
  });
  return data;
}

export async function setTenantTier(
  tenantId: string,
  tier: number,
): Promise<TenantTierResult> {
  const { data } = await apiClient.patch<TenantTierResult>(
    KPI_TENANT_TIER_URL,
    { product_tier: tier },
    { params: { tenant_id: tenantId } },
  );
  return data;
}
